import React from "react";
import { Briefcase, PartyPopper, Palmtree, Hourglass, AlertTriangle, Moon, Clock3, FileClock } from "lucide-react";
import { fmtMin } from "./portalShared";

export const MARCAS = {
  trabajado: { icon: Briefcase, label: "Trabajado", cls: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30" },
  festivo: { icon: PartyPopper, label: "Festivo", cls: "text-pink-300", bg: "bg-pink-500/10 border-pink-500/30" },
  ausencia: { icon: Palmtree, label: "Ausencia aprobada", cls: "text-sky-300", bg: "bg-sky-500/10 border-sky-500/30" },
  compensacion: { icon: Hourglass, label: "Compensación de horas", cls: "text-indigo-200", bg: "bg-indigo-500/10 border-indigo-500/30" },
  anomalia: { icon: AlertTriangle, label: "Anomalía de fichaje", cls: "text-red-300", bg: "bg-red-500/10 border-red-500/40" },
  nocturna: { icon: Moon, label: "Cruza medianoche", cls: "text-violet-300", bg: "" },
  abierta: { icon: Clock3, label: "Jornada abierta", cls: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/30" },
  pendiente: { icon: FileClock, label: "Solicitud pendiente", cls: "text-amber-300", bg: "" },
};
const ORDEN = ["anomalia", "abierta", "compensacion", "ausencia", "festivo", "trabajado"];
const DIAS = ["L", "M", "X", "J", "V", "S", "D"];

const fondoDia = (d) => {
  for (const k of ORDEN) if (d.marcas.includes(k)) return MARCAS[k].bg;
  if (!d.laborable) return "bg-zinc-950/40 border-zinc-800/40";
  return "bg-zinc-900/40 border-zinc-800/60";
};

const ariaDia = (d) => {
  const partes = [new Date(`${d.fecha}T12:00:00`).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })];
  d.marcas.forEach((m) => partes.push(MARCAS[m].label));
  if (d.n_jornadas) partes.push(`${fmtMin(d.trabajados_min)} trabajadas`);
  if (d.esperados_min) partes.push(`${fmtMin(d.esperados_min)} esperadas`);
  if (d.festivo_nombre) partes.push(d.festivo_nombre);
  return partes.join(", ");
};

export function CalendarioMes({ data, onDia }) {
  if (!data) return null;
  const offset = data.dias[0]?.weekday ?? 0;
  const celdas = [...Array(offset).fill(null), ...data.dias];
  return (
    <div data-testid="calendario-mes">
      <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] uppercase tracking-wider text-zinc-500 text-center">
        {DIAS.map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1" role="grid">
        {celdas.map((d, i) => d === null ? <div key={`e${i}`} /> : (
          <button key={d.fecha} onClick={() => onDia(d.fecha)} aria-label={ariaDia(d)} role="gridcell"
            data-testid={`cal-dia-${d.fecha}`} data-marcas={d.marcas.join(" ")}
            className={`relative rounded-lg border p-1 sm:p-1.5 min-h-[56px] sm:min-h-[72px] text-left flex flex-col transition-colors hover:border-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-400 ${fondoDia(d)} ${d.es_hoy ? "ring-1 ring-amber-400/70" : ""} ${d.futuro ? "opacity-70" : ""}`}>
            <div className="flex items-center justify-between">
              <span className={`text-xs font-mono ${d.es_hoy ? "text-amber-300 font-semibold" : d.laborable ? "text-zinc-200" : "text-zinc-500"}`}>{Number(d.fecha.slice(8))}</span>
              {d.n_pendientes > 0 && <span className="text-[9px] text-amber-300 font-semibold" title="Solicitud pendiente" data-testid={`cal-pend-${d.fecha}`}>{d.n_pendientes}</span>}
            </div>
            <div className="flex flex-wrap gap-0.5 mt-auto">
              {d.marcas.filter((m) => m !== "pendiente").map((m) => {
                const M = MARCAS[m];
                return <M.icon key={m} className={`w-3 h-3 ${M.cls}`} aria-hidden="true" />;
              })}
              {d.marcas.includes("pendiente") && <MARCAS.pendiente.icon className="w-3 h-3 text-amber-300" aria-hidden="true" />}
            </div>
            {d.n_jornadas > 0 && (
              <div className="text-[10px] font-mono text-zinc-300 leading-none mt-0.5" data-testid={`cal-horas-${d.fecha}`}>
                {fmtMin(d.trabajados_min)}
                {d.saldo_fiable && d.saldo_min !== 0 && <span className={d.saldo_min > 0 ? "text-emerald-300" : "text-red-300"}> {d.saldo_min > 0 ? "+" : "−"}{fmtMin(Math.abs(d.saldo_min))}</span>}
              </div>
            )}
            {d.n_jornadas === 0 && d.esperados_min === 0 && d.ausencia_tipo && (
              <div className="text-[9px] text-sky-300/80 leading-none truncate hidden sm:block">{(d.ausencias.find((a) => a.estado === "aprobada") || {}).label}</div>
            )}
          </button>
        ))}
      </div>
      <Leyenda />
    </div>
  );
}

function Leyenda() {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-[10px] text-zinc-400" data-testid="calendario-leyenda">
      {Object.entries(MARCAS).map(([k, M]) => (
        <span key={k} className="inline-flex items-center gap-1"><M.icon className={`w-3 h-3 ${M.cls}`} /> {M.label}</span>
      ))}
    </div>
  );
}
